import { Filter as FilterIcon } from "@faire/slate-icons/Filter";
import type { SVGIconProps } from "@faire/slate-icons/types";
import { FilterButton, FilterToggle } from "@faire/slate/components/filter";
import React from "react";

import type { QuickFilter } from "./quickFilters";
import { QUICK_FILTERS } from "./quickFilters";

import { InsiderKeyIcon, TopShopShieldIcon } from "../filters/components/ChipIcons";

interface QuickFilterBarProps {
  appliedKeys: Set<string>;
  onToggle: (key: string) => void;
  onOpenAllFilters: () => void;
  /** Chips appended after the standard row, e.g. query-relevant refinements. */
  extraFilters?: QuickFilter[];
}

export const QuickFilterBar: React.FC<QuickFilterBarProps> = ({
  appliedKeys,
  onToggle,
  onOpenAllFilters,
  extraFilters = [],
}) => (
  <div
    aria-label="Filters"
    className="flex items-center gap-(--slate-dimensions-2) overflow-x-auto"
    role="group"
  >
    {/*
      FilterButton rather than FilterToggle: opening the modal is an action, not
      a state the chip itself holds.
    */}
    <div className="shrink-0">
      <FilterButton
        Icon={FilterIcon}
        label="All filters"
        onClick={onOpenAllFilters}
      />
    </div>

    {chipsInOrder(extraFilters).map((filter) => (
      <div key={filter.key} className="shrink-0">
        <FilterToggle
          Icon={iconFor(filter)}
          applied={appliedKeys.has(filter.key)}
          label={filter.label}
          onClick={() => onToggle(filter.key)}
        />
      </div>
    ))}
  </div>
);

const iconFor = (filter: QuickFilter): React.FC<SVGIconProps> | undefined => {
  if (filter.icon === "insider") {
    return InsiderKeyIcon;
  }
  if (filter.icon === "topShop") {
    return TopShopShieldIcon;
  }
  return undefined;
};

/** Query-relevant chips slot in right after "Low minimum", ahead of the rest. */
const LOW_MINIMUM_KEY = "low-minimum";

const chipsInOrder = (extraFilters: QuickFilter[]): QuickFilter[] => {
  const lowMinimumIndex = QUICK_FILTERS.findIndex(
    (filter) => filter.key === LOW_MINIMUM_KEY
  );
  return [
    ...QUICK_FILTERS.slice(0, lowMinimumIndex + 1),
    ...extraFilters,
    ...QUICK_FILTERS.slice(lowMinimumIndex + 1),
  ];
};
